import { request, rawRequest, buildQuery, orgBase } from '@/api/http'
import type { ChatRequest, ChatResponse } from '@/api/types'

export const aiApi = {
  // Non-streaming grounded Q&A.
  chat(orgId: string, body: ChatRequest): Promise<ChatResponse> {
    return request(`${orgBase(orgId)}/chat`, { method: 'POST', body: JSON.stringify(body) })
  },

  // Streaming Q&A — parses typed SSE frames (`event: <type>\ndata: <json>`) into
  // callbacks: status (a tool is running), token (a text delta), done, error. The
  // token text is JSON-encoded server-side so its own newlines survive framing.
  async chatStream(
    orgId: string,
    body: ChatRequest,
    handlers: {
      onStatus?: (tool: string, label: string) => void
      onToken?: (text: string) => void
      onDone?: () => void
      onError?: (message: string) => void
    },
    signal?: AbortSignal
  ): Promise<void> {
    const resp = await rawRequest(`${orgBase(orgId)}/chat${buildQuery({ stream: true })}`, {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    })
    if (!resp.ok || !resp.body) {
      throw new Error(`[${resp.status}] chat stream failed`)
    }
    const reader = resp.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const frames = buffer.split('\n\n')
      buffer = frames.pop() ?? ''
      for (const frame of frames) {
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'))
        if (!dataLine) continue
        let ev: { type?: string; tool?: string; label?: string; text?: string }
        try {
          ev = JSON.parse(dataLine.slice(5).trim())
        } catch {
          continue
        }
        switch (ev.type) {
          case 'status':
            handlers.onStatus?.(ev.tool ?? '', ev.label ?? '')
            break
          case 'token':
            handlers.onToken?.(ev.text ?? '')
            break
          case 'done':
            handlers.onDone?.()
            break
          case 'error':
            handlers.onError?.(ev.text ?? 'error')
            break
        }
      }
    }
  },
}
